import React, { useState } from 'react';
import { useAuth } from './hooks/useAuth';
import './i18n';
import AuthPage from './pages/AuthPage';
import CreatePasswordPage from './pages/CreatePasswordPage';
import HeaderMobile from './components/HeaderMobile';
import SimpleStreamsPage from './pages/SimpleStreamsPage';
import DiscoverPage from './pages/DiscoverPage';
import MessagesPage from './pages/MessagesPage';
import FriendsPage from './pages/FriendsPage';
import ProfilePage from './pages/ProfilePage';
import AccountPage from './pages/AccountPage';
import JetonPage from './pages/JetonPage';
import WatchStreamPage from './pages/WatchStreamPage';
import StatsPage from './pages/StatsPage';
import LeaderboardPage from './pages/LeaderboardPage';
import TasksPage from './pages/TasksPage';
import InvitePage from './pages/InvitePage';
import SupportPage from './pages/SupportPage';
import { VoiceChatProvider } from './contexts/VoiceChatContext';
import VoiceRoomSession from './components/VoiceRoomSession';
import { BottomNav } from './components/BottomNav';
import { Drawer } from './components/Drawer';
import { GoLiveScreen } from './components/GoLiveScreen';
import ActiveStreamView from './components/ActiveStreamView';
import webRTCService from './services/webrtc';
import { streamAPI } from './services/api';

const originalLog = console.log;
const originalError = console.error;
const remoteLog = (type: string, ...args: any[]) => {
  try {
    const msg = args.map(a => typeof a === 'object' ? JSON.stringify(a) : String(a)).join(' ');
    if (msg.includes('[WebRTC]')) {
      fetch('http://207.154.195.11:5000/api/mobile-log', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ type, message: msg }),
      }).catch(() => null);
    }
  } catch (e) {}
};
console.log = function(...args) {
  originalLog.apply(console, args);
  remoteLog('log', ...args);
};
console.error = function(...args) {
  originalError.apply(console, args);
  remoteLog('error', ...args);
};

const App: React.FC = () => {
  const [currentPage, setCurrentPage] = useState('streams');
  const [showPasswordPage, setShowPasswordPage] = useState(false);
  const [authData, setAuthData] = useState({ email: '', username: '' });
  const [initialVoiceRoomData, setInitialVoiceRoomData] = useState<{ title: string; category: string; type: 'voice' | 'video' } | null>(null);
  const [watchStreamId, setWatchStreamId] = useState<string | null>(null);
  const [fullScreenMode, setFullScreenMode] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [showGoLive, setShowGoLive] = useState(false);
  const [viewProfileId, setViewProfileId] = useState<string | null>(null);
  const [messageUserId, setMessageUserId] = useState<string | null>(null);
  const [history, setHistory] = useState<string[]>(['streams']);
  const { loading, isAuthenticated, user } = useAuth();

  // State refs for Capacitor back button listener
  const stateRef = React.useRef({
    currentPage,
    showGoLive,
    drawerOpen,
    fullScreenMode,
    watchStreamId,
    isAuthenticated,
    isLive: false,
    history: ['streams']
  });

  const [isLive, setIsLive] = useState(false);
  const [mediaStream, setMediaStream] = useState<MediaStream | null>(null);
  const [streamTitle, setStreamTitle] = useState('');
  const [facingMode, setFacingMode] = useState<'user' | 'environment'>('user');
  const videoRef = React.useRef<HTMLVideoElement>(null);

  // Reset full screen mode when changing pages
  React.useEffect(() => {
    if (currentPage !== 'streams') {
      setFullScreenMode(false);
    }
  }, [currentPage]);

  // Keep stateRef synced
  React.useEffect(() => {
    stateRef.current = {
      currentPage,
      showGoLive,
      drawerOpen,
      fullScreenMode,
      watchStreamId,
      isAuthenticated,
      isLive,
      history
    };
  }, [currentPage, showGoLive, drawerOpen, fullScreenMode, watchStreamId, isAuthenticated, isLive, history]);

  // Handle Android Hardware Back Button
  React.useEffect(() => {
    let listener: any = null;
    const initBackButton = async () => {
      try {
        const { App: CapApp } = await import('@capacitor/app');
        listener = await CapApp.addListener('backButton', () => {
          const state = stateRef.current;
          
          if (!state.isAuthenticated) {
            const event = new CustomEvent('appBackButton');
            window.dispatchEvent(event);
            return;
          }

          if (state.drawerOpen) {
            setDrawerOpen(false);
            return;
          }

          if (state.showGoLive) {
            setShowGoLive(false);
            return;
          }

          if (state.isLive) {
            // Cannot exit while streaming directly with back button
            return;
          }

          if (state.fullScreenMode) {
            setFullScreenMode(false);
            return;
          }

          // Allow components to intercept back button
          const event = new CustomEvent('appBackButton', { cancelable: true });
          window.dispatchEvent(event);
          if (event.defaultPrevented) return;

          if (state.currentPage === 'streams' || state.currentPage === 'discover') {
            CapApp.exitApp();
          } else {
            if (state.currentPage === 'watch') {
              setWatchStreamId(null);
            }
            if (state.history.length > 0) {
              const newHistory = [...state.history];
              const prevPage = newHistory.pop() || 'streams';
              setHistory(newHistory);
              setCurrentPage(prevPage);
            } else {
              setCurrentPage('streams');
            }
          }
        });
      } catch (err) {
        console.log('Capacitor App plugin not available', err);
      }
    };
    initBackButton();

    return () => {
      if (listener) listener.remove();
    };
  }, []);

  const handleMainNavigation = (page: string) => {
    if (page === 'profile') {
      setViewProfileId(null);
    }
    if (page !== currentPage) {
      setHistory(prev => {
        const newHistory = [...prev];
        if (newHistory.length === 0 || newHistory[newHistory.length - 1] !== currentPage) {
          newHistory.push(currentPage);
        }
        return newHistory;
      });
    }
    setCurrentPage(page);
  };

  // Add mobile simulator styles
  React.useEffect(() => {
    // Add mobile-specific styles for simulator
    const style = document.createElement('style');
    style.textContent = `
      #root {
        height: 100%;
        overflow: hidden;
      }
      
      /* Hide default scrollbars in simulator */
      ::-webkit-scrollbar {
        display: none;
      }
      
      /* Mobile-friendly font sizes */
      body {
        font-size: 14px;
        -webkit-text-size-adjust: 100%;
      }
      
      /* Touch-friendly buttons */
      button {
        min-height: 44px;
        min-width: 44px;
      }
      
      /* Safe area for iPhone notch */
      .safe-area-top {
        padding-top: env(safe-area-inset-top);
      }
      
      .safe-area-bottom {
        padding-bottom: env(safe-area-inset-bottom);
      }
    `;
    document.head.appendChild(style);
  }, []);

  if (loading) {
    return (
      <div style={styles.loadingContainer}>
        <div style={styles.spinner}></div>
        <p>Yükleniyor...</p>
      </div>
    );
  }

  if (!isAuthenticated) {
    if (showPasswordPage) {
      return <CreatePasswordPage email={authData.email} username={authData.username} />;
    }
    return <AuthPage onShowPassword={(data) => {
      setAuthData(data);
      setShowPasswordPage(true);
    }} />;
  }

  const handleStartVoiceRoom = (data: { title: string; category: string; type: 'voice' | 'video' }) => {
    setInitialVoiceRoomData(data);
    setCurrentPage('discover');
  };

  const handleRoomCreated = () => {
    setInitialVoiceRoomData(null);
  };

  const startVideoStream = async (title: string, category: string) => {
    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({
        video: {
          width: { ideal: 1280, max: 1920 },
          height: { ideal: 720, max: 1080 },
          facingMode: facingMode
        },
        audio: true
      });
    } catch (err) {
      console.error("Camera access denied:", err);
      alert("Kamera erişimi reddedildi veya cihaz bulunamadı.");
      return;
    }

    try {
      setMediaStream(stream);
      setStreamTitle(title);
      setIsLive(true);

      // Notify backend first to get the REAL stream key for the room
      const streamRes = await streamAPI.startStream(title);
      const streamKey = streamRes.stream?.streamKey || user?.streamKey || 'temp-stream-key';
      console.log(`Start Broadcaster - Key: ${streamKey}`);

      // Start broadcasting logic with the correct key
      webRTCService.startBroadcasting(streamKey, stream, user?.id || (user as any)?._id);
    } catch (err) {
      console.error("Error starting stream broadcast:", err);
      // Backend error occurred, but the camera is accessible, so we can fallback 
      // to user's default stream key and attempt broadcasting anyway
      const streamKey = user?.streamKey || 'temp-stream-key';
      webRTCService.startBroadcasting(streamKey, stream, user?.id || (user as any)?._id);
    }
  };

  const handleStopStream = async () => {
    setIsLive(false);
    if (mediaStream) {
      mediaStream.getTracks().forEach(t => t.stop());
      setMediaStream(null);
    }
    webRTCService.leaveRoom();
    await streamAPI.stopStream().catch(console.error);
  };

  const handleFlipCamera = async () => {
    if (!mediaStream) return;
    
    const newFacingMode = facingMode === 'user' ? 'environment' : 'user';
    setFacingMode(newFacingMode);
    
    try {
      const newStream = await navigator.mediaDevices.getUserMedia({
        video: {
          width: { ideal: 1280, max: 1920 },
          height: { ideal: 720, max: 1080 },
          facingMode: newFacingMode
        }
      });
      
      const newVideoTrack = newStream.getVideoTracks()[0];
      if (newVideoTrack) {
        webRTCService.replaceVideoTrack(newVideoTrack);
        // Replace track in mediaStream
        const oldVideoTrack = mediaStream.getVideoTracks()[0];
        if (oldVideoTrack) {
          mediaStream.removeTrack(oldVideoTrack);
          oldVideoTrack.stop();
        }
        mediaStream.addTrack(newVideoTrack);
        
        // Force video element to use updated stream
        if (videoRef.current) {
          videoRef.current.srcObject = mediaStream;
        }
      }
    } catch (err) {
      console.error("Error flipping camera:", err);
      setFacingMode(facingMode); // Revert state on failure
      alert("Kamera değiştirilemedi. Lütfen cihaz izinlerini kontrol edin.");
    }
  };

  const renderPage = () => {
    switch (currentPage) {
      case 'streams':
        // @ts-ignore
        return <SimpleStreamsPage onNavigate={handleMainNavigation} onWatchStream={(id) => { setWatchStreamId(id); setCurrentPage('watch'); }} onStreamStart={() => setFullScreenMode(true)} onStreamStop={() => setFullScreenMode(false)} onDrawer={() => setDrawerOpen(true)} onViewProfile={(id) => { setViewProfileId(id); setCurrentPage('profile'); }} />;
      case 'discover':
        return <DiscoverPage initialRoomData={initialVoiceRoomData} onRoomCreated={handleRoomCreated} onNavigate={handleMainNavigation} onWatchStream={(id) => { setWatchStreamId(id); setCurrentPage('watch'); }} onViewProfile={(id) => { setViewProfileId(id); setCurrentPage('profile'); }} onDrawer={() => setDrawerOpen(true)} />;
      case 'watch':
        return watchStreamId ? <WatchStreamPage streamId={watchStreamId} onViewProfile={(id) => { setViewProfileId(id); setCurrentPage('profile'); setWatchStreamId(null); }} onBack={() => { setCurrentPage('discover'); setWatchStreamId(null); }} /> : <DiscoverPage onDrawer={() => setDrawerOpen(true)} onViewProfile={(id) => { setViewProfileId(id); setCurrentPage('profile'); }} />;
      case 'messages':
        return <MessagesPage />;
      case 'friends':
        return <FriendsPage />;
      case 'profile':
        return <ProfilePage 
          profileUserId={viewProfileId} 
          onNavigate={handleMainNavigation}
          onViewProfile={(id) => { setViewProfileId(id); setCurrentPage('profile'); }}
          onStartVoiceRoom={handleStartVoiceRoom}
          onMessageUser={(id) => { setMessageUserId(id); setCurrentPage('messages'); }} 
        />;
      case 'account':
        return <AccountPage onNavigate={handleMainNavigation} />;
      case 'coins':
        return <JetonPage />;
      case 'stats':
        return <StatsPage onBack={() => setCurrentPage('streams')} />;
      case 'leaderboard':
        return <LeaderboardPage onBack={() => setCurrentPage('streams')} />;
      case 'tasks':
        return <TasksPage onBack={() => setCurrentPage('streams')} />;
      case 'invite':
        return <InvitePage onBack={() => setCurrentPage('streams')} />;
      case 'support':
        return <SupportPage onBack={() => handleMainNavigation('streams')} />;
      default:
        return <DiscoverPage onNavigate={handleMainNavigation} onWatchStream={(id) => { setWatchStreamId(id); setCurrentPage('watch'); }} onViewProfile={(id) => { setViewProfileId(id); setCurrentPage('profile'); }} onDrawer={() => setDrawerOpen(true)} />;
    }
  };

  return (
    <VoiceChatProvider>
      <div id="app-root" style={{
        ...styles.app,
        backgroundColor: '#0f0f13' // Global dark theme
      }}>
        <main style={styles.main}>
          {renderPage()}
        </main>



        {/* Active Voice Session (Persistent) */}
        <VoiceRoomSession />

        {/* Start Stream Modal */}
        {showGoLive && (
          <GoLiveScreen
            onClose={() => setShowGoLive(false)}
            onStart={(data) => {
              setShowGoLive(false);
              if (data.type === 'voice') {
                handleStartVoiceRoom(data);
              } else {
                startVideoStream(data.title, data.category);
              }
            }}
          />
        )}

        {/* Drawer Menu */}
        <Drawer open={drawerOpen} onClose={() => setDrawerOpen(false)} navigate={handleMainNavigation} />

        {/* Explicitly passing style to ensure z-index wins */}
        {!fullScreenMode && !isLive && currentPage !== 'watch' && (
          <BottomNav active={currentPage} navigate={handleMainNavigation} onDrawer={() => setDrawerOpen(true)} onGoLive={() => setShowGoLive(true)} />
        )}

        {/* Video Broadcast Overlay */}
        {isLive && (
          <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, zIndex: 9999 }}>
            <ActiveStreamView
              streamTitle={streamTitle}
              onStopStream={handleStopStream}
              videoRef={videoRef}
              moderators={[]}
              onAddModerator={() => { }}
              onRemoveModerator={() => { }}
              newModeratorName=""
              setNewModeratorName={() => { }}
              user={user}
              mediaStream={mediaStream}
              onFlipCamera={handleFlipCamera}
              onViewProfile={(id) => { setViewProfileId(id); setCurrentPage('profile'); }}
            />
          </div>
        )}
        {/* Modal Root for Portals - Strictly constrained to App layout */}
        <div id="modal-root" style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 10000,
          pointerEvents: 'none', // Allow clicks to pass through if empty
          overflow: 'hidden'
        }}></div>
      </div>
    </VoiceChatProvider>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  app: {
    width: '100%',
    height: '100%',
    backgroundColor: '#0f0f13',
    color: '#ffffff',
    overflow: 'hidden',
    position: 'relative',
    boxSizing: 'border-box',
    fontFamily: '"Inter", "Outfit", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '100vh',
    backgroundColor: '#0f0f13',
  },
  spinner: {
    width: '50px',
    height: '50px',
    border: '5px solid #f3f3f3',
    borderTop: '5px solid #ff007f',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
  },
  main: {
    width: '100%',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    overflowY: 'auto', // Enable vertical scrolling for pages
    overflowX: 'hidden',
    boxSizing: 'border-box',
    zIndex: 1,
    paddingBottom: '85px', // Space for bottom nav + safety margin
  },
};

const styleSheet = document.createElement('style');
styleSheet.textContent = `
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;
document.head.appendChild(styleSheet);

export default App;

